import { useQuery } from '@tanstack/react-query';
import { useBrand } from './useBrand';
import { supabase } from '@/integrations/supabase/client';

export interface BrandFeatures {
  content_engine: boolean;
  social_posts: boolean;
  campaigns: boolean;
  enrichment: boolean;
  buyer_matches: boolean;
  data_cleanup: boolean;
  deduplication: boolean;
  nda_signing: boolean;
  paid_media: boolean;
  campaign_cold_email: boolean;
  campaign_cold_sms: boolean;
  campaign_rvm: boolean;
  campaign_mailers: boolean;
  cold_replies: boolean;
  conversations_v2: boolean;
  buyer_billing: boolean;
  frankly_tipoff: boolean;
}

const DEFAULT_FEATURES: BrandFeatures = {
  content_engine: true,
  social_posts: true,
  campaigns: true,
  enrichment: true,
  buyer_matches: true,
  data_cleanup: true,
  deduplication: true,
  nda_signing: true,
  paid_media: false,
  campaign_cold_email: false,
  campaign_cold_sms: false,
  campaign_rvm: false,
  campaign_mailers: false,
  cold_replies: false,
  conversations_v2: false,
  // Stripe Buyer Network billing (#2132) — OFF until Stripe test-mode env
  // is wired and the flow is verified end-to-end.
  buyer_billing: false,
  // Frankly Tipoff (#2121) — gates the broker-facing Tipoff feed in the
  // notification bell. OFF by default. The pipeline/prefs-page side is
  // gated separately by app_settings.frankly_tipoff_enabled (platform-wide,
  // since the cron drain isn't brand-scoped).
  frankly_tipoff: false,
};

export function useBrandFeatures(): BrandFeatures {
  const { activeBrand } = useBrand();
  if (!activeBrand?.features) return DEFAULT_FEATURES;
  return { ...DEFAULT_FEATURES, ...(activeBrand.features as Partial<BrandFeatures>) };
}

export function useFeatureEnabled(feature: keyof BrandFeatures): boolean {
  const features = useBrandFeatures();
  return features[feature] ?? false;
}

/** Fetch brand features for a specific brand (admin context — may not be the active brand) */
export function useBrandFeaturesById(brandId: string | null) {
  return useQuery({
    queryKey: ['brand-features', brandId],
    queryFn: async () => {
      if (!brandId) return DEFAULT_FEATURES;
      const { data, error } = await supabase
        .from('brands')
        .select('features')
        .eq('id', brandId)
        .single();
      if (error) throw error;
      return { ...DEFAULT_FEATURES, ...(data?.features as Partial<BrandFeatures>) };
    },
    enabled: !!brandId,
    staleTime: 5 * 60_000,
  });
}
